import { Component, OnInit } from '@angular/core';
import { MembersService } from '../members.service';
import { Member } from '../models/member.model';
import { DataTableDirective } from '../../../shared/directives/data-table.directive';
import { ColumnConfig } from '../../../shared/models/column-config.model';
import { AsyncValidatorFn, FormBuilder, Validators } from '@angular/forms';
import { existsValidator } from '../../../shared/validators/exists.validator';
import { ActivatedRoute, Router } from '@angular/router';
import { TitleService } from '../../../shared/services/title.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PageStateManagerService } from '../../../shared/services/page-state-manager.service';
import { AuthService } from '../../../shared/services/auth.service';


@Component({
  selector: 'app-members-list',
  templateUrl: './members-list.component.html',
  standalone: false
})
export class MembersListComponent extends DataTableDirective<Member> implements OnInit {

  override columns: ColumnConfig[] = [];
  override modelName = 'Member';
  notesItem: Member | null = null;

  constructor(
    private membersService: MembersService, 
    fb: FormBuilder, 
    route: ActivatedRoute, 
    router: Router, 
    titleService: TitleService,
    notificationService: NotificationService,
    pageStateManager: PageStateManagerService,
    authService: AuthService,
  ) {
    super(
      membersService, 
      fb, 
      route, 
      router, 
      titleService, 
      notificationService,
      pageStateManager,
      authService
    );       
  }

  override ngOnInit() {
    this.columns = [
      { 
        name: 'firstName', 
        label: 'First Name', 
        inputType: 'text',
        validators: [Validators.required],
        errorMessages: { required: 'First Name is required' }
      },
      { 
        name: 'lastName', 
        label: 'Last Name', 
        inputType: 'text',
        validators: [Validators.required] ,
        errorMessages: { required: 'Last Name is required' } 
      },
      { 
        name: 'email', 
        label: 'Email', 
        inputType: 'email',
        validators: [Validators.required, Validators.email],
        asyncValidators: [existsValidator(this.membersService)],
        updateOn: 'blur',
        errorMessages: { 
          required: 'Email is required', 
          email: 'Please enter a valid email address',
          exists: 'A member with this email address already exists' 
        } 
      },
      { 
        name: 'address', 
        label: 'Address',
        inputType: 'text', 
        validators: [Validators.required],
        errorMessages: { required: 'Address is required' }
      },
      { 
        name: 'town', 
        label: 'Town', 
        inputType: 'text',
        validators: [Validators.required],
        errorMessages: { required: 'Town is required' }
      },
      { 
        name: 'postCode', 
        label: 'Post Code', 
        inputType: 'text',
        validators: [
          Validators.pattern('^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$'),        
          Validators.required
        ],
        errorMessages: {
          required: 'Post Code is required',
          pattern: 'Please enter a valid UK Post Code'
        }
      },
      { 
        name: 'phoneNumber', 
        label: 'Phone', 
        inputType: 'text',
        validators: [Validators.required],
        errorMessages: { required: 'Phone Number is required'}
      },
      { 
        name: 'alternativeNumber', 
        label: 'Alternative Number',
        inputType: 'text'
      },
      { 
        name: 'deposit', 
        label: 'Deposit', 
        inputType: 'currency',
        validators: [          
          Validators.pattern('^\\d+(\\.\\d{1,2})?$'), 
          Validators.min(1)
        ],
        errorMessages: {          
          pattern: 'Deposit must be a valid number with up to 2 decimal places',
          min: 'Deposit must be at least 1'
        }        
      },
      {
        name: 'id',
        label: 'Plots',
        inputType: 'link',
        linkRoute: ['/members', ':param', 'plots'],
      }
    ];
    super.ngOnInit();
  }

  protected override getAsyncValidatorsForEdit(column: ColumnConfig, itemId: number): AsyncValidatorFn[] {
    if (column.name === 'email') {
      return [existsValidator(this.membersService, itemId)];
    }
    return [];
  }

  openNotes(item: Member): void {
    this.notesItem = item;
  }

  onNotesSave(notes: string | null): void {
    if (!this.notesItem) return;
    const updated = { ...this.notesItem, notes };
    this.membersService.update(updated).subscribe({
      next: (saved) => {
        this.notesItem!.notes = saved.notes;
        this.notesItem = null;
        this.notificationService.success('Notes saved successfully');
      },
      error: () => {
        this.notesItem = null;
      }
    });
  }

  onNotesCancel(): void {
    this.notesItem = null;
  }
}
